import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ArrowRight,
  Search,
  Calendar,
  User,
  Clock,
  BookOpen
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/public/blog`);
        const data = response.data || [];
        setPosts(data);
        setFilteredPosts(data);
        
        // Extract unique categories
        const cats = [...new Set(data.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      } catch (err) {
        setPosts([]);
        setFilteredPosts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = [...posts];
    
    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(query) ||
        (p.excerpt && p.excerpt.toLowerCase().includes(query))
      );
    }
    
    setFilteredPosts(filtered);
  }, [activeCategory, searchQuery, posts]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const featuredPost = filteredPosts.find(p => p.is_featured) || filteredPosts[0];
  const otherPosts = filteredPosts.filter(p => p.id !== featuredPost?.id);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-16 lg:pt-48 lg:pb-20"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), white, var(--color-hero-bg-to, #EFF6FF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
            >
              <BookOpen className="w-4 h-4" />
              Marketing Insights
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Insights & Ideas
            </h1>
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              Practical marketing insights and strategies drawn from over 40 years of 
              experience in brand building, digital marketing, and creative leadership.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 border-b border-slate-100 sticky top-20 bg-white z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('all')}
                style={activeCategory === 'all' ? { backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' } : {}}
                data-testid="filter-all"
              >
                All Posts
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={activeCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveCategory(cat)}
                  style={activeCategory === cat ? { backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' } : {}}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search articles..." 
                className="pl-10 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && !searchQuery && activeCategory === 'all' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Link to={`/blog/${featuredPost.slug}`} data-testid="featured-post">
              <Card className="border-0 shadow-xl overflow-hidden group cursor-pointer" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative aspect-video md:aspect-auto overflow-hidden">
                    <img 
                      src={featuredPost.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop'} 
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <Badge style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }} className="absolute top-4 left-4">Featured</Badge>
                  </div>
                  <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                    {featuredPost.category && (
                      <Badge variant="outline" className="w-fit mb-4">{featuredPost.category}</Badge>
                    )}
                    <h2 
                      className="text-2xl md:text-3xl font-bold mb-4 transition-colors"
                      style={{ color: 'var(--color-heading-text, #0F172A)' }}
                    >
                      {featuredPost.title}
                    </h2>
                    <p className="mb-6 line-clamp-3" style={{ color: 'var(--color-body-text, #475569)' }}>{featuredPost.excerpt}</p>
                    <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>
                      {featuredPost.author_name && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {featuredPost.author_name}
                        </div>
                      )}
                      {featuredPost.published_at && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(featuredPost.published_at)}
                        </div>
                      )}
                      {featuredPost.reading_time && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {featuredPost.reading_time}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div 
                className="animate-spin rounded-full h-12 w-12 border-b-2"
                style={{ borderColor: 'var(--color-primary, #1F3C4F)' }}
              ></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-section-alt-bg, #F1F5F9)' }}
              >
                <BookOpen className="w-10 h-10" style={{ color: 'var(--color-muted-text, #94A3B8)' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-heading-text, #0F172A)' }}>
                Insights Coming Soon
              </h3>
              <p className="text-lg max-w-md mx-auto" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>
                We're working on fresh marketing insights and strategies. Check back soon for our latest articles.
              </p>
              <Link to="/contact" className="mt-6 inline-block">
                <Button 
                  style={{ 
                    backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                    color: 'var(--color-button-text, #FFFFFF)',
                    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                  }}
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>No articles found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(searchQuery || activeCategory !== 'all' ? filteredPosts : otherPosts).map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <Card 
                    className="border-0 shadow-lg overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-shadow"
                    style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img 
                        src={post.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      {post.category && (
                        <Badge variant="outline" className="mb-3">{post.category}</Badge>
                      )}
                      <h3 
                        className="text-xl font-bold mb-3 transition-colors line-clamp-2"
                        style={{ color: 'var(--color-heading-text, #0F172A)' }}
                      >
                        {post.title}
                      </h3>
                      <p className="text-sm line-clamp-2 mb-4" style={{ color: 'var(--color-body-text, #475569)' }}>{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>
                        {post.published_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.published_at)}
                          </div>
                        )}
                        {post.reading_time && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.reading_time}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ color: 'var(--color-heading-text, #0F172A)' }}
          >
            Stay Updated
          </h2>
          <p 
            className="text-xl mb-8"
            style={{ color: 'var(--color-body-text, #475569)' }}
          >
            Get the latest marketing insights and strategies delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input 
              type="email" 
              placeholder="Enter your email" 
              className="flex-1"
              data-testid="newsletter-email"
            />
            <Button 
              data-testid="newsletter-subscribe"
              style={{ 
                backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                color: 'var(--color-button-text, #FFFFFF)',
                borderRadius: 'var(--btn-computed-radius, 0.5rem)'
              }}
            >
              Subscribe
            </Button>
          </div>
          <p className="text-sm mt-4" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>
            No spam. Unsubscribe at any time.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default BlogPage;
